/**
 * @deprecated Since version 2.0.0 - Use TanStack Query instead
 * 
 * This file contains legacy NgRx reducer that is no longer maintained.
 * 
 * Migration Guide:
 * - TanStack Query manages state internally via queries and mutations
 * - No need for manual state reducers
 * - Loading states: use `query.isPending()` or `query.isFetching()`
 * - Error states: use `query.isError()` and `query.error()`
 * - Data: use `query.data()`
 * - Optimistic updates: handled by mutation `onSuccess` callbacks
 * 
 * Example migration:
 * ```typescript
 * // OLD (NgRx):
 * interface LocationsState {
 *   loading: boolean;
 *   error: string | null;
 *   locationRootList: LocationModel[] | null;
 * }
 * 
 * // NEW (TanStack Query):
 * locationsQuery = inject(WarehousesQueriesService).useWarehousesList(filters);
 * isLoading = computed(() => this.locationsQuery.isPending());
 * hasError = computed(() => this.locationsQuery.isError());
 * locations = computed(() => this.locationsQuery.data()?.results || []);
 * ```
 * 
 * See documentation: libs/kanzi-location/docs/FASE-1-RESUMEN.md
 */
import { LocationFilterModel, LocationModel } from '@kanzi-apes/kanzi-models';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';

import * as LocationsActions from './locations.actions';
import { LocationsEntity } from './locations.models';

/**
 * @deprecated Use TanStack Query cache key instead
 */
export const LOCATIONS_FEATURE_KEY = 'locationsRoot';

/**
 * @deprecated Use TanStack Query state management
 */
export interface LocationsState extends EntityState<LocationsEntity> {
  selectedId?: string | number; // which Locations record has been selected
  loading: boolean; // has the Locations list been loaded
  error?: string | null; // last known error (if any)
  locationRootList: LocationModel[] | null;
  locationRootFilter: LocationFilterModel | null;
  locationRootSelected: LocationModel | null;
}

/**
 * @deprecated No longer needed with TanStack Query
 */
export interface LocationsPartialState {
  readonly [LOCATIONS_FEATURE_KEY]: LocationsState;
}

/**
 * @deprecated Use TanStack Query cache instead
 */
export const locationsAdapter: EntityAdapter<LocationsEntity> =
  createEntityAdapter<LocationsEntity>();

/**
 * @deprecated Use TanStack Query default state
 */
export const initialLocationsState: LocationsState =
  locationsAdapter.getInitialState({
    loading: false,
    locationRootList: null,
    locationRootSelected: null,
    locationRootFilter: null,
    error: null,
  });

/**
 * @deprecated Use TanStack Query mutations and cache updates instead
 */
const reducer = createReducer(
  initialLocationsState,
  on(LocationsActions.initAuth, (state) => ({
    ...state,
    loading: false,
    locationRootList: null,
    error: null,
  })),
  on(LocationsActions.loadLocationRootList, (state, { filters }) => ({
    ...state,
    locationRootFilter: filters,
    loading: true,
  })),
  on(LocationsActions.loadLocationRootListSuccess, (state, { listItems }) => ({
    ...state,
    loading: false,
    locationRootList: listItems,
  })),

  on(LocationsActions.loadLocationRootListFailure, (state, { error }) => ({
    ...state,
    error: error,
  })),
  on(LocationsActions.selectLocationRoot, (state, { location }) => ({
    ...state,
    locationRootSelected: location,
  })),
  on(LocationsActions.locationIdSelected, (state, { locationId }) => ({
    ...state,
    locationRootSelected:
      state.locationRootList?.find((item) => item.id === locationId) || null,
  }))
);

/**
 * @deprecated Use TanStack Query instead - no reducer function needed
 */
export function locationsReducer(
  state: LocationsState | undefined,
  action: Action
) {
  return reducer(state, action);
}
